/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { OLAPTransformerResult, useParameter, useDictionary, Column, useDictionaryItem } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { dictionaryService } from "../../../dataFlow";
import { orderBy, uniqBy } from "lodash";
import { Parameters } from "../Context";
import { colors, RICE, SLIDE_NUMBER } from "../constants";
import { getDateId, getDateWithoutFrequency, formatTimestampDate } from "../utils/dateHelpers";

const CATEGORIES_AXIS = "calendar.default";
const SERIES_AXIS = "units.default.name";
const futureSeriesId = ["ending_stocks_fc", "fc_price", "cbot_price"];
const actualSeriesId = ["ending_stocks", "historical_price"];

const cbotPricesSoucreId = "facts_cbot_prices";
interface ICategory {
  calendarId: number;
  name: string;
  index: number;
  isFcDate: boolean;
}
export function useHighchartTransformer(
  olapData: OLAPTransformerResult | null,
  parameters: Parameters | null | undefined,
): Highcharts.Options | undefined {
  const [state, setState] = useState<Highcharts.Options>();
  const [calendarItems] = useDictionary(dictionaryService, "calendar-origin");
  const [dictItemsLastYear] = useDictionary(dictionaryService, "slides-f-last-calendar", undefined, {
    commodities: parameters ? String(parameters.commodities) : null,
    slide: SLIDE_NUMBER,
  });
  const [selectedCommodityId] = useParameter<number>("@selected_commodity_id");
  const dictItemsLastDataYear = Number(
    (
      orderBy(dictItemsLastYear || [], (item) => item.getValue<number>("order"))[0] || {
        id: getDateId(new Date(new Date().getFullYear(), 0, 1), 1),
      }
    ).id,
  );
  const [cbotDateValue] = useParameter<number>("@cbot_date");
  const [, cbotDateName] = useDictionaryItem(dictionaryService, "slides-cbot-date-slide34", cbotDateValue);
  const [sourcesDictItems] = useDictionary(dictionaryService, "slides-sources");
  const cbotForecastPriceLastUpdated = formatTimestampDate(
    sourcesDictItems?.find((sourceItem) => sourceItem.id === cbotPricesSoucreId)?.getValue<string>("lastUpdate"),
  );

  const olapColumns = useMemo(() => {
    const allColumns = [olapData].filter(Boolean).flatMap((e) => e?.columns);
    const uniqColumns = uniqBy(allColumns, (e) => e?.selection[CATEGORIES_AXIS]).filter(Boolean);
    const primarySortedColumns = orderBy(uniqColumns, (e) => e?.selection[CATEGORIES_AXIS]);
    const secondarySortedColumns = orderBy(primarySortedColumns, (e) =>
      String(e?.selection[CATEGORIES_AXIS]).slice(1, 5),
    );
    return secondarySortedColumns as Column[];
  }, [olapData]);

  const categories = useMemo(() => {
    const сalendarIds = olapColumns.map((colum) => colum.selection[CATEGORIES_AXIS]);

    const categories: ICategory[] = [];
    сalendarIds.forEach((calendarId, index) => {
      const calendarItem = calendarItems?.find((item) => Number(item.id) === Number(calendarId));
      const name = calendarItem?.getValue<string>("name") || "";
      if (Number(calendarId) === dictItemsLastDataYear) {
        categories.push({
          calendarId: Number(calendarId),
          name: name + "act",
          index: categories.length,
          isFcDate: false,
        });
        categories.push({
          calendarId: Number(calendarId),
          name: "FC" + name,
          index: categories.length,
          isFcDate: true,
        });
      } else {
        categories.push({
          calendarId: Number(calendarId),
          name,
          index: categories.length,
          isFcDate:
            getDateWithoutFrequency(Number(calendarId)) < getDateWithoutFrequency(dictItemsLastDataYear) ? false : true,
        });
      }
    });

    return categories;
  }, [olapColumns, calendarItems]);

  const options = useChartOptions(
    {
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function() {
          if (this.points && this.points[0]) {
            let tooltipContent = `<b>${this.points[0].point.category}</b><br/>`;

            const displaySeries: string[] = actualSeriesId.includes((this.points[0].point as any).serieId)
              ? actualSeriesId
              : futureSeriesId;
            this.points.forEach(({ point }) => {
              if (point && displaySeries.includes((point as any).serieId)) {
                let pointValue = Highcharts.numberFormat(point.y || 0, 1);
                tooltipContent += `<b>${(point as any).serieName}</b>: <span>${pointValue} ${
                  (point as any).unit
                }</span><br/>`;
              }
            });
            return `<div>${tooltipContent}</div>`;
          }
          return "";
        },
        outside: true,
        style: {
          zIndex: 100,
        },
      },
    },
    [
      chartPipe(),
      legendPipe("bottom", {
        useHTML: true,
        labelFormatter: function() {
          const { name } = this;
          return name;
        },
      }),
      addXAxisPipe(
        categories.map((category) => category.name),
        {
          visible: true,
          labels: {
            enabled: true,
            formatter: function() {
              const label = this.axis.defaultLabelFormatter.call(this);
              return label;
            },
          },
        },
      ),
    ],
  );

  useEffect(() => {
    if (olapData && olapData.rows[0] && olapData.rows[1]) {
      const forecastSeriesName = `CBOT ${cbotForecastPriceLastUpdated}`;
      const cbotSeriesName = `СВОТ ${cbotDateName}`;
      const historicSeriesName = `Historic prices`;
      const endingStocksName = `Ending stocks`;
      const endingStocksFCName = `FC Ending stocks`;
      const rightTitleY = Object.values(olapData.rows[1].attributes[SERIES_AXIS])[0]
      const historicalPricesSeries = {
        name: historicSeriesName,
        data:
          categories
            ?.filter((category) => !category.isFcDate)
            .map((category, categoryIndex) => {
              const point = olapData.rows[1].points.find(
                (p) => p.column.selection[CATEGORIES_AXIS] === category.calendarId,
              );
              const value = point?.historical_price || null;
              return {
                x: category.index,
                y: value,
                date: category.name,
                serieName: historicSeriesName,
                unit:
                  point?.attributes && point?.attributes["units.default.name"]
                    ? point?.attributes["units.default.name"][9]
                    : "",
                serieId: "historical_price",
              };
            }) || [],
        yAxis: 0,
        xAxis: 0,
        color: colors[1],
        zIndex: 2,
        marker: {
          radius: 0,
        },
        showInLegend: true,
        states: {
          hover: {
            lineWidth: 0,
            lineWidthPlus: 0,
          },
        },
        dataLabels: {
          enabled: false,
        },
      } as any;

      const forecastPricesSeries = {
        name: forecastSeriesName,
        data:
          categories
            ?.filter((category) => category.isFcDate || category.calendarId === dictItemsLastDataYear)
            .map((category, categoryIndex) => {
              const point = olapData.rows[1].points.find(
                (p) => p.column.selection[CATEGORIES_AXIS] === category.calendarId,
              );
              let value = point?.fc_price || null;

              if (category.calendarId === dictItemsLastDataYear && !category.isFcDate) {
                value = point?.historical_price || null;
              }

              return {
                x: category.index,
                y: value,
                date: category.name,
                serieName: forecastSeriesName,
                unit:
                  point?.attributes && point?.attributes["units.default.name"]
                    ? point?.attributes["units.default.name"][9]
                    : "",
                serieId: "fc_price",
              };
            }) || [],
        yAxis: 0,
        xAxis: 0,
        color: colors[0],
        zIndex: 2,
        marker: {
          radius: 0,
        },
        showInLegend: true,
        states: {
          hover: {
            lineWidth: 0,
            lineWidthPlus: 0,
          },
        },
        dataLabels: {
          enabled: false,
        },
      } as any;

      const cbotPricesSeries = {
        name: cbotSeriesName,
        data:
          categories
            ?.filter((category) => category.isFcDate)
            .map((category, categoryIndex) => {
              const point = olapData.rows[1].points.find(
                (p) => p.column.selection[CATEGORIES_AXIS] === category.calendarId,
              );
              const value = point?.cbot_price || null;
              return {
                x: category.index,
                y: value,
                date: category.name,
                serieName: cbotSeriesName,
                unit:
                  point?.attributes && point?.attributes["units.default.name"]
                    ? point?.attributes["units.default.name"][9]
                    : "",
                serieId: "cbot_price",
              };
            }) || [],
        yAxis: 0,
        xAxis: 0,
        color: colors[2],
        zIndex: 2,
        marker: {
          radius: 0,
        },
        showInLegend: true,
        states: {
          hover: {
            lineWidth: 0,
            lineWidthPlus: 0,
          },
        },
        dataLabels: {
          enabled: false,
        },
      } as any;

      const endingStockSeries = {
        name: endingStocksName,
        type: "column",
        data:
          categories
            ?.filter((category) => !category.isFcDate)
            .map((category) => {
              const point = olapData.rows[0].points.find(
                (p) => p.column.selection[CATEGORIES_AXIS] === category.calendarId,
              );
              const value = point?.ending_stocks || null;
              return {
                x: category.index,
                y: value,
                date: category.name,
                serieName: endingStocksName,
                unit:
                  point?.attributes && point?.attributes["units.default.name"]
                    ? point?.attributes["units.default.name"][9]
                    : "",
                serieId: actualSeriesId[0],
              };
            }) || [],
        yAxis: 1,
        xAxis: 0,
        color: colors[3],
        zIndex: 2,
        marker: {
          radius: 0,
        },
        showInLegend: true,
        states: {
          hover: {
            lineWidth: 0,
            lineWidthPlus: 0,
          },
        },
        dataLabels: {
          enabled: false,
        },
      } as any;

      const endingStockFCSeries = {
        name: endingStocksFCName,
        type: "column",
        data:
          categories
            ?.filter((category) => category.isFcDate)
            .map((category) => {
              const point = olapData.rows[0].points.find(
                (p) => p.column.selection[CATEGORIES_AXIS] === category.calendarId,
              );
              const value = point?.ending_stocks || null;
              return {
                x: category.index,
                y: value,
                date: category.name,
                serieName: endingStocksName,
                unit:
                  point?.attributes && point?.attributes["units.default.name"]
                    ? point?.attributes["units.default.name"][9]
                    : "",
                serieId: futureSeriesId[0],
              };
            }) || [],
        yAxis: 1,
        xAxis: 0,
        color: "transparent",
        borderColor: colors[3],
        borderWidth: 2,
        dashStyle: "dash",
        zIndex: 2,
        marker: {
          radius: 0,
        },
        showInLegend: true,
        states: {
          hover: {
            lineWidth: 0,
            lineWidthPlus: 0,
          },
        },
        dataLabels: {
          enabled: false,
        },
      } as any;

      const result: Highcharts.Options = {
        ...options,
        chart: {
          ...options.chart,
          marginTop: 30,
        },
        plotOptions: {
          column: {
            stacking: "normal",
          },
        },
        series: [
          endingStockSeries,
          endingStockFCSeries,
          historicalPricesSeries,
          forecastPricesSeries,
          cbotPricesSeries,
        ],
        yAxis: [
          {
            gridLineColor: "#fff",
            title: {
              text: rightTitleY,
              align: "high",
              offset: 0,
              rotation: 0,
              y: -10,
            },
            labels: {
              enabled: true,
              formatter: function() {
                const label = this.axis.defaultLabelFormatter.call(this);

                return label;
              },
            },
          },
          {
            opposite: true,
            gridLineColor: "#fff",
            title: {
              text: "MMT",
              align: "high",
              offset: 0,
              rotation: 0,
              y: -10,
            },
          },
        ],
      };
      setState(result);
    } else {
      setState(undefined);
    }
  }, [
    olapData,
    olapColumns,
    categories,
    calendarItems,
    cbotDateName,
    dictItemsLastDataYear,
    cbotForecastPriceLastUpdated,
  ]);
  return state;
}
